import React from "react"
import { Avatar, Badge, Box, Card, Divider, Group, Text, Timeline, Tooltip } from "@mantine/core"
import { LoadMoreButton } from "src/components/ui/LoadMoreButton"
import { EmptyPageComponent } from "src/components/ui/Misc"
import { ModuleErrors } from "src/components/ui/ModuleErrors"
import { PageFilters, usePageFilters } from "src/components/ui/PageFilters"
import { useActions, useAppState } from "src/state"
import { MessageFragment, Message_Type_Enum, Messages_Bool_Exp } from "src/state/gql/_types"
import { MessageActionMeta } from "src/state/messages/_types"
import { formatDate, timeAgo } from "src/utils/dates"
import { MessagesTimeline } from "src/components/ui/MessagesTimeline"
import { Icon } from "@iconify/react"
import { CreateSuperAdminThreadMessageInline } from "src/components/superadmin/threads/messages/SuperAdminThreadMessageActions"
import { createClickFilterEnumValues } from "src/components/ui/PageFilters"

const clickFilterEnumValues = createClickFilterEnumValues<Message_Type_Enum, Messages_Bool_Exp>(Message_Type_Enum,'type')

const TIMELINE_BULLET_SIZE = 37

export function SuperAdminThreadMessages() {

    const { itemsList, errors, meta, loadingMore, meta: { limit, offset, sortKey, sortOrder } } = useAppState(state => state.messages)
    const {
        subscribeToItems,
        unsubscribeFromItems,
        getMoreItems,
        resetModule,
    } = useActions().messages
    const { markThreadRead } = useActions().threads
    const { currentItem: currentThread } = useAppState(state => state.threads)
    const userProfile = useAppState().profile

    const {
        filters,
        filterParams
    } = usePageFilters<Messages_Bool_Exp['_and'], MessageFragment>({
        baseFilters: [{
            thread_id: { _eq: currentThread?.id }
        }],
        clickFilters: [],
        searchFilterFields: [],
        searchFilterRelations: {},
        clickFilterField: 'type',
        clickFilterValues: clickFilterEnumValues
    })

    React.useEffect(() => {
        subscribeToItems(filters);
        return () => {
            unsubscribeFromItems()
        }
    }, [filters, limit, offset, sortKey, sortOrder, subscribeToItems, unsubscribeFromItems, resetModule])

    React.useEffect(() => {
        if (!currentThread) return;
        if ((currentThread.unread_count ?? 0) > 0) {
            markThreadRead({ thread_id: currentThread.id });
        }
    }, [currentThread?.id, currentThread?.unread_count]);

    // const handleMetaChange = (meta: SetMetaInput<MessageFragment>) => {
    //     setMeta(meta)
    // }

    // const handleSelect = (id: string) => {
    //     selectItem(id)
    // }

    const renderMessage = (i: MessageFragment) => {
        let isAuthor = i.author_id === i.thread.created_by
        return (
            <Card
                shadow="xs"
                p="sm"
                flex={1}
            >
                <Group gap="5">
                    <Text size="sm" fw={600}>{i.author?.name}</Text>
                    <Tooltip
                        label={formatDate(i.created_at, 'MMM D, YYYY, h:mm A')}
                        arrowOffset={50}
                        arrowSize={8}
                        withArrow
                    >
                        <Text size="sm" c="dark.2">{timeAgo(i.created_at)}</Text>
                    </Tooltip>
                    {isAuthor && (
                        <Tooltip
                            label="This is the author of this thread"
                            arrowOffset={50}
                            arrowSize={8}
                            withArrow
                        >
                            <Badge variant="default" ml="auto">Author</Badge>
                        </Tooltip>
                    )}
                </Group>
                <Divider my="xs" variant="dashed" />
                <Text size="sm">{i.body}</Text>
            </Card>
        )
    }

    const getActionLabel = (meta: MessageActionMeta): string => {
        switch (meta.action) {
            case 'booking_created':
                return `Booking created as ${meta.status}`
            case 'booking_updated':
                return `Booking updated to ${meta.status}`
            case 'status_changed':
                return `Status changed from ${meta.from_status} to ${meta.to_status}`
            default:
                return 'System action'
        }
    }

    const getActionIcon = (meta: MessageActionMeta): string => {
        switch (meta.action) {
            case 'booking_created':
                return 'ph:plus-circle'
            case 'booking_updated':
                return 'ph:pencil-simple'
            case 'status_changed':
                return 'ph:arrow-circle-right'
            default:
                return 'ph:gear'
        }
    }

    const renderAction = (i: MessageFragment) => {
        const meta = i.meta as MessageActionMeta | null
        const label = meta ? getActionLabel(meta) : i.body

        return (
            <Box
                p="xs"
                flex={1}
            >
                <Group gap="5">
                    <Text size="xs" fw={600}>{i.author?.name}: {label}</Text>
                    <Tooltip
                        label={formatDate(i.created_at, 'MMM D, YYYY, h:mm A')}
                        arrowOffset={50}
                        arrowSize={8}
                        withArrow
                    >
                        <Text size="xs" c="dark.2">{timeAgo(i.created_at)}</Text>
                    </Tooltip>
                </Group>
            </Box>
        )
    }

    const renderVirtualAction = (title: string, message: string, date: string) => {
        return (
            <Box
                p="xs"
                flex={1}
            >
                <Group gap="5">
                    <Text size="xs" fw={600}>{title}</Text>
                    <Text size="xs">{message}</Text>
                    <Tooltip
                        label={formatDate(date, 'MMM D, YYYY, h:mm A')}
                        arrowOffset={50}
                        arrowSize={8}
                        withArrow
                    >
                        <Text size="xs" c="dark.2">{timeAgo(date)}</Text>
                    </Tooltip>
                </Group>
            </Box>
        )
    }

    const renderCards = () => {
        return (
            <React.Fragment>
                <MessagesTimeline
                    bulletSize={TIMELINE_BULLET_SIZE}
                >
                    <Timeline.Item
                        bullet={<Icon icon="ph:gear" width={18} />}
                    >
                        <Box >
                            {renderVirtualAction(currentThread?.created_by_user?.name || '', 'Created this thread', currentThread?.created_at)}
                        </Box>
                    </Timeline.Item>
                    {itemsList.map((i, k) => {

                        let isAction = i.type === Message_Type_Enum.Action

                        return (
                            <Timeline.Item
                                bullet={
                                    <>
                                        {!isAction ? (
                                            <Avatar
                                                name={`${i.author?.name}`}
                                                color="initials"
                                                size={TIMELINE_BULLET_SIZE}
                                            />

                                        ) : (
                                            <Icon icon={getActionIcon(i.meta as MessageActionMeta)} width={18} />
                                        )}
                                    </>
                                }
                            >
                                <Box key={k}>
                                    {isAction ? renderAction(i) : renderMessage(i)}
                                </Box>
                            </Timeline.Item>
                        )
                    })}
                    <Timeline.Item
                        title="Post a Reply"
                        bullet={
                            <Avatar
                                name={`${userProfile?.name}`}
                                color="initials"
                                size={TIMELINE_BULLET_SIZE}
                            />
                        }
                    >
                        <CreateSuperAdminThreadMessageInline />
                    </Timeline.Item>
                </MessagesTimeline>
                <LoadMoreButton meta={meta} onLoadMore={getMoreItems} loading={loadingMore} />
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            <PageFilters options={filterParams} />
            <ModuleErrors errors={errors} />
            {itemsList.length ? (<> {renderCards()} </>) : (<EmptyPageComponent label='Messages' />)}
        </React.Fragment>
    )

}